import { useEffect, useRef } from 'react';

const services = [
  {
    icon: 'ri-tools-line',
    title: 'Kombi Tamiri',
    desc: 'Kombi arıza tespiti, parça değişimi ve onarım işlemleri. Elektronik kart, eşanjör, brülör, pompa gibi tüm parça değişimleri yerinde yapılır.',
    tag: 'En Çok Talep',
  },
  {
    icon: 'ri-cpu-line',
    title: 'Anakart Tamiri',
    desc: 'Kombi anakart arızası en sık karşılaşılan elektronik sorunların başında gelir. Yanmış kart, hata kodu veren devre, sensör arızası gibi tüm anakart sorunları yerinde teşhis ve onarım ile çözülür.',
    tag: 'Elektronik Arıza',
  },
  {
    icon: 'ri-settings-4-line',
    title: 'Kombi Bakımı',
    desc: 'Yılda en az bir kez periyodik bakım ile cihazınızın ömrünü uzatın. Daha az yakıt, daha verimli ısınma ve sorunsuz bir kış.',
    tag: null,
  },
  {
    icon: 'ri-home-gear-line',
    title: 'Kombi Montajı',
    desc: 'Yeni kombi montajı, sökme-takma işlemleri ve devreye alma. Güvenlik kurallarına uygun, sertifikalı ekiplerimizle hizmetinizdeyiz.',
    tag: null,
  },
  {
    icon: 'ri-temp-cold-line',
    title: 'Petek Temizliği',
    desc: "Petek temizliği ve kombi devresi bakımı ile ısınma performansınızı artırın. %30'a varan enerji tasarrufu sağlayın.",
    tag: null,
  },
  {
    icon: 'ri-fire-line',
    title: 'Brülör & Gaz Valfi Tamiri',
    desc: 'Kombi tutuşmuyor, alev gitmiyor veya gaz valfi arızalı mı? Brülör temizliği, ateşleme elektrodu ve gaz valfi değişimi uzman ekibimizle aynı gün yapılır.',
    tag: null,
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('opacity-100', 'translate-y-0');
          e.target.classList.remove('opacity-0', 'translate-y-6');
        }
      }),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="w-full py-16 md:py-24 bg-[#f0f7ff]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-primary bg-white border border-[#dbeafe] px-3 py-1.5 rounded-full uppercase tracking-widest mb-4">
            Hizmetlerimiz
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">
            Tüm kombi ihtiyaçlarınız tek merkezden
          </h2>
          <p className="text-sm text-slate-500 max-w-lg mx-auto">
            Yerinde kombi tamiri, bakım ve montaj. Uzman ustalar ve şeffaf fiyat anlayışıyla her zaman yanınızdayız.
          </p>
        </div>

        {/* Visual banner */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 mb-10 rounded-2xl overflow-hidden relative h-[220px] md:h-[260px]">
          <img
            src="/images/services-banner-boiler-v1.jpg"
            alt="Kombi Servis Hizmetleri"
            className="w-full h-full object-cover object-center"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent flex items-center px-8 md:px-12">
            <div>
              <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Profesyonel Ekip</p>
              <h3 className="text-white text-2xl md:text-3xl font-extrabold leading-tight mb-3">
                Yerinde Kombi Tamiri<br />ve Bakım Hizmeti
              </h3>
              <a
                href="tel:05332836679"
                className="inline-flex items-center gap-2 bg-white text-primary font-bold text-sm px-5 py-2.5 rounded-full hover:bg-slate-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-fill" />
                Hemen Ara
              </a>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <div
              key={s.title}
              className="anim opacity-0 translate-y-6 transition-all duration-500 relative bg-white rounded-2xl p-7 border border-slate-100 hover:border-primary/25 hover:-translate-y-1 hover:shadow-lg transition-all group cursor-pointer overflow-hidden"
              style={{ transitionDelay: `${i * 80}ms` }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {/* Subtle bg accent */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/[0.03] rounded-bl-full" />

              {s.tag && (
                <span className="absolute top-4 right-4 text-[10px] font-bold text-primary bg-[#f0f7ff] px-2 py-0.5 rounded-full">
                  {s.tag}
                </span>
              )}

              <div className="w-12 h-12 flex items-center justify-center bg-primary/[0.06] text-primary rounded-2xl mb-5 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <i className={`${s.icon} text-xl`} />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">{s.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>

              <div className="mt-5 flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                Servis Al <i className="ri-arrow-right-line" />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA bar */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 mt-10 bg-primary rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-white font-bold text-lg mb-1">Uygun Fiyat Garantisi</p>
            <p className="text-white/70 text-sm">İşe başlamadan önce net fiyat, sürpriz maliyet yok.</p>
          </div>
          <a
            href="tel:05332836679"
            className="inline-flex items-center gap-2 bg-white text-primary font-bold text-sm px-7 py-3.5 rounded-full hover:bg-slate-50 transition-colors whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-phone-fill" />
            Hemen Ara
          </a>
        </div>
      </div>
    </section>
  );
}